import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import { 
  Users, Plus, Mail, Clipboard, CheckCircle, AlertCircle, 
  ArrowRight, Copy, RefreshCw, Eye
} from 'lucide-react';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';
import toast from 'react-hot-toast';
import GuideModal from './GuideModal';

const CoachDashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  
  // State for team management
  const [team, setTeam] = useState(null);
  const [coachProfile, setCoachProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State for team creation and editing
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false);
  const [showEditTeamForm, setShowEditTeamForm] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [updatingTeam, setUpdatingTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamSport, setTeamSport] = useState('');
  const [teamAgeGroup, setTeamAgeGroup] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamDescription, setEditTeamDescription] = useState('');
  const [editTeamSport, setEditTeamSport] = useState('');
  const [editTeamAgeGroup, setEditTeamAgeGroup] = useState('');
  const [editTeamSize, setEditTeamSize] = useState('');
  
  // State for team members
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSortField, setMemberSortField] = useState('name');
  const [memberSortDirection, setMemberSortDirection] = useState('asc');
  
  // State for invitations
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [showInvitationForm, setShowInvitationForm] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [invitationName, setInvitationName] = useState('');
  const [invitationEmail, setInvitationEmail] = useState('');
  const [invitationRole, setInvitationRole] = useState('Athlete');
  const [resendingInvitation, setResendingInvitation] = useState(null);
  const [cancelingInvitation, setCancelingInvitation] = useState(null);
  const [invitationSortField, setInvitationSortField] = useState('createdAt');
  const [invitationSortDirection, setInvitationSortDirection] = useState('desc');
  const [copiedCode, setCopiedCode] = useState('');
  
  // State for MVP and Mindset modals
  const [showMVPModal, setShowMVPModal] = useState(false);
  const [showMindsetModal, setShowMindsetModal] = useState(false);
  const [selectedMemberMVP, setSelectedMemberMVP] = useState(null);
  const [selectedMemberMindset, setSelectedMemberMindset] = useState(null);
  const [loadingMVP, setLoadingMVP] = useState(false);
  const [loadingMindset, setLoadingMindset] = useState(false);

  // Fetch team data on component mount
  useEffect(() => {
    fetchTeamData();
    
    // Track dashboard view
    Mixpanel.trackEnhancedPageView('/coach-dashboard', 'Coach Dashboard', {
      feature: 'Coach Dashboard',
      action: 'Opened'
    });
  }, []);
  
  // Fetch team data from the server
  const fetchTeamData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.warn('CoachDashboard: No token found, redirecting to login');
        navigate('/login');
        return;
      }
      
      logger.debug('CoachDashboard: Fetching team data');
      
      const response = await axios.get(`${API_URL}/api/teams/coach`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });
      
      logger.debug('CoachDashboard: Team data response', { 
        status: response.status,
        hasTeam: !!response.data?.team 
      });
      
      if (response.data && response.data.team) {
        logger.debug('CoachDashboard: Team data details', { 
          teamId: response.data.team._id,
          teamName: response.data.team.name,
          sport: response.data.team.sport,
          ageGroup: response.data.team.ageGroup,
          teamSize: response.data.team.size
        });
        
        setTeam(response.data.team);
        // Set coach profile data if available
        if (response.data.coachProfile) {
          logger.debug('CoachDashboard: Coach profile data', response.data.coachProfile);
          setCoachProfile(response.data.coachProfile);
        }
        // If team exists, fetch invitations and team members
        fetchInvitations(response.data.team._id);
        fetchTeamMembers(response.data.team._id);
      } else {
        logger.debug('CoachDashboard: No team found in response data');
        setTeam(null);
        setShowCreateTeamForm(true);
      }
    } catch (err) {
      const status = err.response?.status;
      const errorMessage = err.response?.data?.message || err.message;
      
      logger.error('Error fetching team data', { 
        error: err, 
        status,
        message: errorMessage,
        stack: err.stack
      });
      
      if (status === 404) {
        // No team found, show create team form
        logger.debug('CoachDashboard: 404 response - No team found, showing create form');
        setTeam(null);
        setShowCreateTeamForm(true);
        // Clear any previous errors since this is an expected case
        setError('');
      } else if (status === 401 || status === 403) {
        // Authentication or authorization issue
        logger.warn('CoachDashboard: Auth error, redirecting to login', { status });
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Failed to load your team data. ${errorMessage || 'Please try again later.'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch invitations for a team
  const fetchInvitations = async (teamId) => {
    setLoadingInvitations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/invitations/team/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInvitations(response.data || []);
    } catch (err) {
      logger.error('Error fetching invitations', err);
      // Don't show error for invitations, just log it
    } finally {
      setLoadingInvitations(false);
    }
  };
  
  // Fetch team members for a team
  const fetchTeamMembers = async (teamId) => {
    setLoadingMembers(true);
    try {
      const token = localStorage.getItem('token');
      
      logger.debug('CoachDashboard: Starting to fetch team members for team', teamId);
      
      // First, try to get the debug information to diagnose the issue
      try {
        logger.debug(`CoachDashboard: Fetching debug info from /api/users/debug/team/${teamId}`);
        const debugResponse = await axios.get(`${API_URL}/api/users/debug/team/${teamId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        logger.debug('Debug team members response:', JSON.stringify(debugResponse.data, null, 2));
        
        // Log specific parts of the debug data
        if (debugResponse.data) {
          logger.debug('Team info:', debugResponse.data.teamInfo);
          logger.debug('Member info count:', debugResponse.data.memberInfo ? debugResponse.data.memberInfo.length : 0);
          logger.debug('Members by ID count:', debugResponse.data.membersByIds ? debugResponse.data.membersByIds.length : 0);
          logger.debug('Members by teamId count:', debugResponse.data.membersByTeamId ? debugResponse.data.membersByTeamId.length : 0);
          
          // Check if any members have status 'active'
          const activeMembers = debugResponse.data.memberInfo ? 
            debugResponse.data.memberInfo.filter(m => m.status === 'active') : [];
          logger.debug('Active members count:', activeMembers.length);
          
          // Check if users exist for the active member IDs
          if (activeMembers.length > 0 && debugResponse.data.membersByIds) {
            const activeMemberIds = activeMembers.map(m => m.userId);
            const foundUsers = debugResponse.data.membersByIds.filter(u => 
              activeMemberIds.includes(u.id.toString())
            );
            logger.debug('Found users for active members:', foundUsers.length);
          }
        }
      } catch (debugErr) {
        logger.warn('Error fetching debug info:', debugErr);
      }
      
      // Also fetch all users directly to ensure we have the most up-to-date data
      logger.debug(`CoachDashboard: Fetching team members from /api/users/team/${teamId}`);
      const usersResponse = await axios.get(`${API_URL}/api/users/team/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log the users response
      if (usersResponse.data) {
        logger.debug('Users response array:', Array.isArray(usersResponse.data));
        logger.debug('Users count:', Array.isArray(usersResponse.data) ? usersResponse.data.length : 
                    (usersResponse.data.members ? usersResponse.data.members.length : 0));
        
        // Log each user
        if (Array.isArray(usersResponse.data) && usersResponse.data.length > 0) {
          usersResponse.data.forEach((user, idx) => {
            logger.debug(`User ${idx}:`, {
              id: user._id,
              name: user.name,
              email: user.email
            });
          });
        } else if (usersResponse.data.members && Array.isArray(usersResponse.data.members)) {
          usersResponse.data.members.forEach((user, idx) => {
            logger.debug(`User ${idx}:`, {
              id: user._id,
              name: user.name,
              email: user.email
            });
          });
        }
      }
      
      let allMembers = [];
      
      // Process users response first (this is the primary source of user data)
      if (usersResponse.data && Array.isArray(usersResponse.data)) {
        logger.debug('Team users response:', usersResponse.data);
        logger.debug('CoachDashboard: Got users from direct endpoint', { count: usersResponse.data.length });
        
        // Add all users from the direct users endpoint
        usersResponse.data.forEach(user => {
          allMembers.push({
            _id: user._id,
            id: user._id,
            name: user.name,
            email: user.email,
            role: user.role,
            createdAt: user.createdAt,
            loginCount: user.loginCount || 0,
            dailyJournals: user.dailyJournals || 0,
            weeklyJournals: user.weeklyJournals || 0,
            performanceJournals: user.performanceJournals || 0,
            breathCycles: user.breathCycles || 0,
            meditationMinutes: user.meditationMinutes || 0,
            concentrationGames: user.concentrationGames || 0,
            habitTrack: user.habitTrack || 0
          });
        });
      } else if (usersResponse.data && usersResponse.data.members && Array.isArray(usersResponse.data.members)) {
        // Handle the case where the response has a members array inside it
        logger.debug('Team users response (nested):', usersResponse.data.members);
        logger.debug('CoachDashboard: Got users from nested members array', { count: usersResponse.data.members.length });
        
        // Add all users from the nested members array
        usersResponse.data.members.forEach(user => {
          allMembers.push({
            _id: user._id,
            id: user._id,
            name: user.name,
            email: user.email,
            role: user.role,
            createdAt: user.createdAt,
            loginCount: user.loginCount || 0,
            dailyJournals: user.dailyJournals || 0,
            weeklyJournals: user.weeklyJournals || 0,
            performanceJournals: user.performanceJournals || 0,
            breathCycles: user.breathCycles || 0,
            meditationMinutes: user.meditationMinutes || 0,
            concentrationGames: user.concentrationGames || 0,
            habitTrack: user.habitTrack || 0
          });
        });
      }
      
      // Then enhance with stats if available
      logger.debug(`CoachDashboard: Fetching team stats from /api/teams/${teamId}/members/stats`);
      const statsResponse = await axios.get(`${API_URL}/api/teams/${teamId}/members/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log the stats response
      if (statsResponse.data) {
        logger.debug('Stats response success:', statsResponse.data.success);
        logger.debug('Stats members count:', statsResponse.data.stats ? statsResponse.data.stats.length : 0);
        
        // Log each member from stats
        if (statsResponse.data.stats && statsResponse.data.stats.length > 0) {
          statsResponse.data.stats.forEach((member, idx) => {
            logger.debug(`Stats member ${idx}:`, {
              _id: member._id || member.id,
              name: member.name,
              email: member.email
            });
          });
        }
        // List of performance metric fields to merge
        const performanceFields = [
          'totalLogins',
          'dailyJournalCount',
          'weeklyJournalCount',
          'performanceJournalCount',
          'breathCyclesCount',
          'meditationMinutes',
          'concentrationGamesPlayed',
          'weeklyCompletionRate',
          'breathworkSessionsCompleted',
          'journalEntriesCreated'
        ];
        // Update existing members with stats data and merge new performance metrics
        statsResponse.data.stats.forEach(statsMember => {
          const existingMemberIndex = allMembers.findIndex(member => 
            (member._id && statsMember.userId && member._id.toString() === statsMember.userId.toString()) || 
            (member._id && statsMember.id && member._id.toString() === statsMember.id.toString()) || 
            (member.email && statsMember.email && member.email === statsMember.email)
          );
          if (existingMemberIndex >= 0) {
            // Update existing member with stats and performance metrics
            const updatedMember = {
              ...allMembers[existingMemberIndex],
              weeklyStats: statsMember.weeklyStats || {},
              totalStats: statsMember.totalStats || {}
            };
            performanceFields.forEach(field => {
              if (statsMember[field] !== undefined) {
                updatedMember[field] = statsMember[field];
              }
            });
            allMembers[existingMemberIndex] = updatedMember;
          } else {
            // Add new member from stats if not already in the list
            const newMember = {
              _id: statsMember.id || statsMember.userId,
              id: statsMember.id || statsMember.userId,
              name: statsMember.name,
              email: statsMember.email,
              role: statsMember.role || 'athlete',
              createdAt: statsMember.registrationDate,
              joinedTeamDate: statsMember.joinedTeamDate,
              dailyJournals: statsMember.totalStats?.journalsCompleted || 0,
              breathCycles: statsMember.totalStats?.breathCycles || 0,
              meditationMinutes: statsMember.totalStats?.meditationMinutes || 0,
              concentrationGames: statsMember.totalStats?.concentrationGames || 0,
              weeklyStats: statsMember.weeklyStats || {},
              totalStats: statsMember.totalStats || {}
            };
            performanceFields.forEach(field => {
              if (statsMember[field] !== undefined) {
                newMember[field] = statsMember[field];
              }
            });
            allMembers.push(newMember);
          }
        });
      } else {
        logger.warn('CoachDashboard: Stats response not successful or missing data', statsResponse.data);
      }
      
      // Log each member in the final list
      if (allMembers.length > 0) {
        allMembers.forEach((member, idx) => {
          logger.debug(`Final member ${idx}:`, {
            id: member._id || member.id,
            name: member.name,
            email: member.email
          });
        });
      }
      
      // If we still have no members but we know the team exists, add a placeholder
      if (allMembers.length === 0) {
        logger.warn('No members found for team, but team exists. Check if API is returning data correctly.');
      }
      
      setTeamMembers(allMembers);
      
    } catch (err) {
      logger.error('Error fetching team members', err);
      logger.error('Error details:', { 
        message: err.message,
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        } : 'No response',
        request: err.request ? 'Request was made but no response received' : 'No request'
      });
      setTeamMembers([]);
      // Show error message to user
      toast.error('Failed to load team members. Please try refreshing the page.');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Function to handle view member details
  const handleViewMember = (memberId) => {
    // Navigate to member profile or show details modal
    navigate(`/athlete/${memberId}`);
    
    // Track view member action
    Mixpanel.track('View Team Member', {
      feature: 'Team Management',
      action: 'View Member Details'
    });
  };

  // Function to fetch and display team member's MVP data
  const handleViewMemberMVP = async (memberId, memberName) => {
    setLoadingMVP(true);
    setShowMVPModal(true);
    setSelectedMemberMVP(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/team-member/${memberId}/mvp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedMemberMVP(response.data);
      
      // Track MVP view action
      Mixpanel.track('View Team Member MVP', {
        feature: 'Team Management',
        action: 'View MVP',
        memberId: memberId,
        memberName: memberName
      });
      
    } catch (error) {
      logger.error('Error fetching team member MVP:', error);
      toast.error('Failed to load team member MVP data');
      setShowMVPModal(false);
    } finally {
      setLoadingMVP(false);
    }
  };

  // Function to fetch and display team member's Mindset data
  const handleViewMemberMindset = async (memberId, memberName) => {
    setLoadingMindset(true);
    setShowMindsetModal(true);
    setSelectedMemberMindset(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/team-member/${memberId}/mindset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedMemberMindset(response.data);
      
      // Track Mindset view action
      Mixpanel.track('View Team Member Mindset', {
        feature: 'Team Management',
        action: 'View Mindset',
        memberId: memberId,
        memberName: memberName
      });
      
    } catch (error) {
      logger.error('Error fetching team member Mindset:', error);
      toast.error('Failed to load team member Mindset data');
      setShowMindsetModal(false);
    } finally {
      setLoadingMindset(false);
    }
  };

  // Function to close MVP modal
  const closeMVPModal = () => {
    setShowMVPModal(false);
    setSelectedMemberMVP(null);
  };

  // Function to close Mindset modal
  const closeMindsetModal = () => {
    setShowMindsetModal(false);
    setSelectedMemberMindset(null);
  };

  // Create a new team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setCreatingTeam(true);
    setError('');
    
    try {
      if (!teamName.trim()) {
        setError('Team name is required');
        setCreatingTeam(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/teams`, 
        {
          name: teamName,
          description: teamDescription,
          sport: teamSport,
          ageGroup: teamAgeGroup,
          size: teamSize
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.team) {
        setTeam(response.data.team);
        setShowCreateTeamForm(false);
        setSuccess('Team created successfully!');
        
        // Track team creation
        Mixpanel.track('Team Created', {
          feature: 'Team Management',
          action: 'Team Created',
          teamName: teamName
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      logger.error('Error creating team', err);
      setError('Failed to create team. Please try again.');
    } finally {
      setCreatingTeam(false);
    }
  };
  
  // Update an existing team
  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    setUpdatingTeam(true);
    setError('');
    setSuccess('');

    try {
      if (!editTeamName.trim()) {
        setError('Team name is required');
        setUpdatingTeam(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/teams/${team._id}`,
        {
          name: editTeamName,
          description: editTeamDescription,
          sport: editTeamSport,
          ageGroup: editTeamAgeGroup,
          size: editTeamSize
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTeam(response.data.team);
      setSuccess('Team updated successfully!');
      setShowEditTeamForm(false);
      Mixpanel.track('Team Updated', { teamId: team._id });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update team.';
      setError(errorMessage);
      logger.error('Error updating team:', { error: err, message: errorMessage });
    } finally {
      setUpdatingTeam(false);
    }
  };

  // Send invitation to an athlete
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    
    if (!invitationEmail) {
      setError('Email is required to send an invitation');
      return;
    }
    
    setSendingInvitation(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/invitations`, {
        email: invitationEmail,
        name: invitationName,
        teamId: team._id,
        role: invitationRole.toLowerCase()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Track invitation sent
      Mixpanel.track('Invitation Sent', {
        feature: 'Coach Dashboard',
        action: 'Send Invitation',
        role: invitationRole
      });
      
      setSuccess('Invitation sent successfully!');
      setInvitationEmail('');
      setInvitationName('');
      setInvitationRole('Athlete');
      setShowInvitationForm(false);
      
      // Refresh invitations list
      fetchInvitations(team._id);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(`Failed to send invitation: ${errorMessage}`);
      
      logger.error('Error sending invitation', { 
        error: err,
        email: invitationEmail
      });
    } finally {
      setSendingInvitation(false);
    }
  };
  
  // Copy invitation code to clipboard
  const copyInvitationCode = (code) => {
    const invitationLink = `${process.env.REACT_APP_FRONTEND_URL || 'https://www.mentaledgecoach.net'}/new-profile?invitation=${code}`;
    navigator.clipboard.writeText(invitationLink).then(() => {
      setCopiedCode(code);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedCode(null);
      }, 2000);
      
      // Track copy event
      Mixpanel.track('Copy Invitation Link', {
        feature: 'Team Management',
        action: 'Copy Invitation Link'
      });
    }).catch(err => {
      logger.error('Failed to copy invitation link', err);
      setError('Failed to copy invitation link to clipboard');
    });
  };
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to get sort icon for members
  const getMemberSortIcon = (field) => {
    if (memberSortField !== field) return null;
    
    return (
      <span className="ml-1 inline-block">
        {memberSortDirection === 'asc' ? 
          <ArrowRight size={14} className="rotate-90" /> : 
          <ArrowRight size={14} className="-rotate-90" />
        }
      </span>
    );
  };

  // Helper function to get sort icon for invitations
  const getInvitationSortIcon = (field) => {
    if (invitationSortField !== field) return null;
    
    return (
      <span className="ml-1 inline-block">
        {invitationSortDirection === 'asc' ? 
          <ArrowRight size={14} className="rotate-90" /> : 
          <ArrowRight size={14} className="-rotate-90" />
        }
      </span>
    );
  };

  // Handle member sorting
  const handleMemberSort = (field) => {
    if (memberSortField === field) {
      setMemberSortDirection(memberSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMemberSortField(field);
      setMemberSortDirection('asc');
    }
  };

  // Handle invitation sorting
  const handleInvitationSort = (field) => {
    if (invitationSortField === field) {
      setInvitationSortDirection(invitationSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInvitationSortField(field);
      setInvitationSortDirection('asc');
    }
  };

  // Get sorted members
  const sortedMembers = [...teamMembers].sort((a, b) => {
    let aValue = a[memberSortField];
    let bValue = b[memberSortField];
    
    // Handle null or undefined values
    if (aValue === undefined || aValue === null) aValue = '';
    if (bValue === undefined || bValue === null) bValue = '';
    
    // Handle special cases
    if (memberSortField === 'role') {
      aValue = a.role || 'athlete';
      bValue = b.role || 'athlete';
    }
    
    // Handle date comparisons
    if (memberSortField === 'joinedTeamDate' || memberSortField === 'createdAt' || memberSortField === 'lastLogin') {
      return memberSortDirection === 'asc' 
        ? new Date(aValue || 0) - new Date(bValue || 0)
        : new Date(bValue || 0) - new Date(aValue || 0);
    }
    
    // Handle numeric comparisons
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return memberSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle string comparisons
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return memberSortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Default comparison
    return memberSortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (bValue > aValue ? 1 : -1);
  });

  // Get sorted invitations
  const sortedInvitations = [...invitations]
    .filter(invitation => invitation.status !== 'registered') // Filter out registered invitations
    .sort((a, b) => {
      let aValue = a[invitationSortField];
      let bValue = b[invitationSortField];
      
      // Handle date comparisons
      if (invitationSortField === 'createdAt' || invitationSortField === 'expiresAt') {
        return invitationSortDirection === 'asc' 
          ? new Date(aValue || 0) - new Date(bValue || 0)
          : new Date(bValue || 0) - new Date(aValue || 0);
      }
      
      // Handle string comparisons
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return invitationSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Default comparison
      return invitationSortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (bValue > aValue ? 1 : -1);
    });

  useEffect(() => {
    if (team) {
      setEditTeamName(team.name || '');
      setEditTeamDescription(team.description || '');
      setEditTeamSport(team.sport || '');
      setEditTeamAgeGroup(team.ageGroup || '');
      setEditTeamSize(team.size || '');
    }
  }, [team]);

  // Handle resending invitation
  const handleResendInvitation = async (invitationId) => {
    setResendingInvitation(invitationId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/invitations/${invitationId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Invitation resent successfully!');
      
      // Track resend event
      Mixpanel.track('Invitation Resent', {
        feature: 'Coach Dashboard',
        action: 'Resend Invitation'
      });
      
      // Refresh invitations list
      fetchInvitations(team._id);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(`Failed to resend invitation: ${errorMessage}`);
      
      logger.error('Error resending invitation', { 
        error: err,
        invitationId
      });
    } finally {
      setResendingInvitation(null);
    }
  };

  // Handle canceling invitation
  const handleCancelInvitation = async (invitationId) => {
    setCancelingInvitation(invitationId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/users/cancel-invitation/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Invitation canceled successfully!');
      
      // Track cancel event
      Mixpanel.track('Invitation Canceled', {
        feature: 'Coach Dashboard',
        action: 'Cancel Invitation'
      });
      
      // Refresh invitations list
      fetchInvitations(team._id);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(`Failed to cancel invitation: ${errorMessage}`);
      
      logger.error('Error canceling invitation', { 
        error: err,
        invitationId
      });
    } finally {
      setCancelingInvitation(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <GuideModal guideKey="CoachDashboard" />
            <h1 className="text-3xl font-bold leading-tight text-gray-900">Coach Dashboard</h1>
          </div>
        </header>
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-6 sm:px-0">
              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Team Info Card */}
                  {team ? (
                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                        <div>
                          <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {team.name}
                          </h3>
                          <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Team details and information
                          </p>
                        </div>
                        <button
                          onClick={() => setShowEditTeamForm(!showEditTeamForm)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {showEditTeamForm ? 'Cancel' : 'Edit Team'}
                        </button>
                      </div>
                      <div className="border-t border-gray-200 px-4 py-5">
                        {showEditTeamForm ? (
                          <div className="px-4 py-5">
                            <form onSubmit={handleUpdateTeam} className="space-y-4">
                              <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                  Team Name
                                </label>
                                <input
                                  type="text"
                                  id="name"
                                  value={editTeamName}
                                  onChange={(e) => setEditTeamName(e.target.value)}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                  Description
                                </label>
                                <textarea
                                  id="description"
                                  value={editTeamDescription}
                                  onChange={(e) => setEditTeamDescription(e.target.value)}
                                  rows={3}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="sport" className="block text-sm font-medium text-gray-700">
                                    Sport
                                  </label>
                                  <input
                                    type="text"
                                    id="sport"
                                    value={editTeamSport}
                                    onChange={(e) => setEditTeamSport(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-700">
                                    Age Group
                                  </label>
                                  <input
                                    type="text"
                                    id="ageGroup"
                                    value={editTeamAgeGroup}
                                    onChange={(e) => setEditTeamAgeGroup(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  />
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="size" className="block text-sm font-medium text-gray-700">
                                    Team Size
                                  </label>
                                  <input
                                    type="text"
                                    id="size"
                                    value={editTeamSize}
                                    onChange={(e) => setEditTeamSize(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="e.g., 15-20 players"
                                  />
                                </div>
                              </div>
                              
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => setShowEditTeamForm(false)}
                                  className="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  disabled={updatingTeam}
                                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  {updatingTeam ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                      Updating...
                                    </>
                                  ) : (
                                    'Update Team'
                                  )}
                                </button>
                              </div>
                            </form>
                          </div>
                        ) : (
                          <dl className="sm:divide-y sm:divide-gray-200">
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Sport</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {team.sport || 'Not specified'}
                              </dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Age Group</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {team.ageGroup || 'Not specified'}
                              </dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Team Size</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {team.size || 'Not specified'}
                              </dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Description</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {team.description || 'No description provided'}
                              </dd>
                            </div>
                          </dl>
                        )}
                      </div>
                    </div>
                  ) : (
                    showCreateTeamForm && (
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className="px-4 py-5 sm:px-6">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Create Your Team
                          </h3>
                          <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Set up your team to start inviting athletes
                          </p>
                        </div>
                        <div className="border-t border-gray-200 px-4 py-5">
                          <form onSubmit={handleCreateTeam} className="space-y-4">
                            <div>
                              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Team Name
                              </label>
                              <input
                                type="text"
                                id="name"
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                              />
                            </div>
                            
                            <div>
                              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                              </label>
                              <textarea
                                id="description"
                                value={teamDescription}
                                onChange={(e) => setTeamDescription(e.target.value)}
                                rows={3}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="sport" className="block text-sm font-medium text-gray-700">
                                  Sport
                                </label>
                                <input
                                  type="text"
                                  id="sport"
                                  value={teamSport}
                                  onChange={(e) => setTeamSport(e.target.value)}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                              </div>
                              
                              <div>
                                <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-700">
                                  Age Group
                                </label>
                                <input
                                  type="text"
                                  id="ageGroup"
                                  value={teamAgeGroup}
                                  onChange={(e) => setTeamAgeGroup(e.target.value)}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="size" className="block text-sm font-medium text-gray-700">
                                  Team Size
                                </label>
                                <input
                                  type="text"
                                  id="size"
                                  value={teamSize}
                                  onChange={(e) => setTeamSize(e.target.value)}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  placeholder="e.g., 15-20 players"
                                />
                              </div>
                            </div>
                            
                            <div className="flex justify-end">
                              <button
                                type="submit"
                                disabled={creatingTeam}
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                {creatingTeam ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                    Creating...
                                  </>
                                ) : (
                                  'Create Team'
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      {team && (
        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Team Members ({teamMembers.length})
          </h3>
          {loadingMembers ? (
            <p>Loading members...</p>
          ) : teamMembers.length === 0 ? (
            <p>No members have joined your team yet.</p>
          ) : (
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-0" onClick={() => handleMemberSort('name')}>
                        <div className="flex items-center">
                          <span className="truncate">Name</span>
                          {getMemberSortIcon('name')}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-0" onClick={() => handleMemberSort('email')}>
                        <div className="flex items-center">
                          <span className="truncate">Email</span>
                          {getMemberSortIcon('email')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16" onClick={() => handleMemberSort('role')}>
                        <div className="flex items-center">
                          <span className="truncate">Role</span>
                          {getMemberSortIcon('role')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" onClick={() => handleMemberSort('concentrationGamesPlayed')}>
                        <div className="flex items-center">
                          <span className="truncate">Games</span>
                          {getMemberSortIcon('concentrationGamesPlayed')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" onClick={() => handleMemberSort('breathworkSessionsCompleted')}>
                        <div className="flex items-center">
                          <span className="truncate">Breath</span>
                          {getMemberSortIcon('breathworkSessionsCompleted')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" onClick={() => handleMemberSort('journalEntriesCreated')}>
                        <div className="flex items-center">
                          <span className="truncate">Journal</span>
                          {getMemberSortIcon('journalEntriesCreated')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20" onClick={() => handleMemberSort('meditationMinutes')}>
                        <div className="flex items-center">
                          <span className="truncate">Relax</span>
                          {getMemberSortIcon('meditationMinutes')}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedMembers.map((member) => (
                      <tr key={member._id}>
                        <td className="px-3 py-4 text-sm font-medium text-gray-900 min-w-0">
                          <div className="truncate">{member.name}</div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 min-w-0">
                          <div className="truncate">{member.email}</div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-500 w-16">
                          <div className="truncate">{member.role}</div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-500 text-center w-20">{member.concentrationGamesPlayed ?? 0}</td>
                        <td className="px-2 py-4 text-sm text-gray-500 text-center w-20">{member.breathworkSessionsCompleted ?? 0}</td>
                        <td className="px-2 py-4 text-sm text-gray-500 text-center w-20">{member.journalEntriesCreated ?? 0}</td>
                        <td className="px-2 py-4 text-sm text-gray-500 text-center w-20">{member.meditationMinutes ?? 0}</td>
                        <td className="px-3 py-4 text-sm font-medium w-40">
                          <div className="flex space-x-1">
                            <button onClick={() => handleViewMemberMVP(member._id, member.name)} className="text-green-600 hover:text-green-900 text-xs">
                              <Clipboard size={14} className="inline mr-1" /> MVP
                            </button>
                            <button onClick={() => handleViewMemberMindset(member._id, member.name)} className="text-purple-600 hover:text-purple-900 text-xs">
                              <Clipboard size={14} className="inline mr-1" /> Mindset
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {team && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Invitations ({sortedInvitations.length})
            </h3>
            <button
              onClick={() => setShowInvitationForm(!showInvitationForm)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus size={18} className="mr-2" /> {showInvitationForm ? 'Cancel' : 'Send Invitation'}
            </button>
          </div>

          {showInvitationForm && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-base leading-6 font-medium text-gray-900">Send New Invitation</h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5">
                <form onSubmit={handleSendInvitation} className="space-y-4">
                  <div>
                    <label htmlFor="invitationName" className="block text-sm font-medium text-gray-700">Athlete Name</label>
                    <input type="text" id="invitationName" value={invitationName} onChange={(e) => setInvitationName(e.target.value)} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                  </div>
                  <div>
                    <label htmlFor="invitationEmail" className="block text-sm font-medium text-gray-700">Athlete Email</label>
                    <input type="email" id="invitationEmail" value={invitationEmail} onChange={(e) => setInvitationEmail(e.target.value)} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                  </div>
                  <div>
                    <label htmlFor="invitationRole" className="block text-sm font-medium text-gray-700">Role</label>
                    <select id="invitationRole" value={invitationRole} onChange={(e) => setInvitationRole(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                      <option>Athlete</option>
                      {/* Add other roles if applicable, e.g., Assistant Coach */}
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={sendingInvitation}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {sendingInvitation ? 'Sending...' : 'Send Invitation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {loadingInvitations ? (
            <p>Loading invitations...</p>
          ) : invitations.length === 0 && !showInvitationForm ? (
            <p>No pending invitations.</p>
          ) : invitations.length > 0 ? (
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleInvitationSort('name')}>
                        <div className="flex items-center">
                          <span className="truncate">Name</span>
                          {getInvitationSortIcon('name')}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleInvitationSort('email')}>
                        <div className="flex items-center">
                          <span className="truncate">Email</span>
                          {getInvitationSortIcon('email')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleInvitationSort('status')}>
                        <div className="flex items-center">
                          <span className="truncate">Status</span>
                          {getInvitationSortIcon('status')}
                        </div>
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        Code
                      </th>
                      <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleInvitationSort('createdAt')}>
                        <div className="flex items-center">
                          <span className="truncate">Sent</span>
                          {getInvitationSortIcon('createdAt')}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedInvitations.map((invitation) => (
                      <tr key={invitation._id}>
                        <td className="px-3 py-4 text-sm font-medium text-gray-900 min-w-0">
                          <div className="truncate">{invitation.name || 'N/A'}</div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 min-w-0">
                          <div className="truncate">{invitation.email}</div>
                        </td>
                        <td className="px-2 py-4 text-sm w-20">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invitation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : invitation.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {invitation.status}
                          </span>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-500 w-24">
                          <div className="flex items-center">
                            <span className="truncate text-xs">{invitation.code}</span>
                            <button onClick={() => copyInvitationCode(invitation.code)} className="ml-1 text-blue-500 hover:text-blue-700 flex-shrink-0">
                              {copiedCode === invitation.code ? <CheckCircle size={12} className="inline"/> : <Copy size={12} className="inline"/>}
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-500 w-20">
                          <div className="truncate text-xs">{formatDate(invitation.createdAt)}</div>
                        </td>
                        <td className="px-3 py-4 text-sm font-medium w-32">
                          {invitation.status === 'pending' && (
                            <div className="flex flex-col space-y-1">
                              <button 
                                onClick={() => handleResendInvitation(invitation._id)} 
                                disabled={resendingInvitation === invitation._id}
                                className="text-green-600 hover:text-green-900 text-xs flex items-center"
                              >
                                {resendingInvitation === invitation._id ? (
                                  <span>Resending...</span>
                                ) : (
                                  <>
                                    <RefreshCw size={12} className="inline mr-1"/>
                                    Resend
                                  </>
                                )}
                              </button>
                              <button 
                                onClick={() => handleCancelInvitation(invitation._id)} 
                                disabled={cancelingInvitation === invitation._id}
                                className="text-red-600 hover:text-red-900 text-xs flex items-center"
                              >
                                {cancelingInvitation === invitation._id ? (
                                  <span>Canceling...</span>
                                ) : (
                                  <>
                                    <AlertCircle size={12} className="inline mr-1"/>
                                    Delete
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                          {invitation.status === 'accepted' && <span className="text-green-600 text-xs">Accepted</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
      {process.env.NODE_ENV !== 'production' && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 001-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Debug Info:</strong> 
                Team ID: {team?._id || 'None'} | 
                Team Members: {teamMembers?.length || 0} | 
                Loading: {loadingMembers ? 'Yes' : 'No'} | 
                Team Exists: {team ? 'Yes' : 'No'}
              </p>
              {teamMembers?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-sm text-yellow-700 cursor-pointer">Show Team Members</summary>
                  <pre className="mt-2 text-xs bg-yellow-50 p-2 overflow-auto max-h-40">
                    {JSON.stringify(teamMembers.map(m => ({
                      id: m._id || m.id,
                      name: m.name,
                      email: m.email,
                      role: m.role,
                      status: m.status
                    })), null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* MVP Modal */}
      {showMVPModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedMemberMVP?.member?.name}'s MVP
                </h3>
                <button
                  onClick={closeMVPModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              
              {loadingMVP ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Loading MVP data...</span>
                </div>
              ) : selectedMemberMVP ? (
                <div className="space-y-6 overflow-y-auto max-h-[60vh]">
                  {/* Mission */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Mission</h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md text-sm">
                      {selectedMemberMVP.mvp?.mission || 'No mission statement available'}
                    </p>
                  </div>
                  
                  {/* Vision */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Vision</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMVP.mvp?.vision && Array.isArray(selectedMemberMVP.mvp.vision) && selectedMemberMVP.mvp.vision.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMVP.mvp.vision.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No vision statements available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Process Goals (Principles) */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Process Goals</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMVP.mvp?.principles && Array.isArray(selectedMemberMVP.mvp.principles) && selectedMemberMVP.mvp.principles.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMVP.mvp.principles.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No process goals available</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 text-center py-8">No MVP data available for this team member.</p>
              )}
              
              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={closeMVPModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Mindset Modal */}
      {showMindsetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedMemberMindset?.member?.name}'s Mindset
                </h3>
                <button
                  onClick={closeMindsetModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              
              {loadingMindset ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Loading Mindset data...</span>
                </div>
              ) : selectedMemberMindset ? (
                <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                  {/* One Word Focus */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">One Word Focus</h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md text-sm">
                      {selectedMemberMindset.mindset?.oneWordFocus || 'No one word focus available'}
                    </p>
                  </div>
                  
                  {/* Keys to Keep It Simple */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Keys to Keep It Simple</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMindset.mindset?.keysToKeepItSimple && Array.isArray(selectedMemberMindset.mindset.keysToKeepItSimple) && selectedMemberMindset.mindset.keysToKeepItSimple.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMindset.mindset.keysToKeepItSimple.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No keys available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Affirmations */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Affirmations</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMindset.mindset?.affirmations && Array.isArray(selectedMemberMindset.mindset.affirmations) && selectedMemberMindset.mindset.affirmations.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMindset.mindset.affirmations.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No affirmations available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Self Talk */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Self Talk</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMindset.mindset?.selfTalk && Array.isArray(selectedMemberMindset.mindset.selfTalk) && selectedMemberMindset.mindset.selfTalk.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMindset.mindset.selfTalk.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No self talk available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Beliefs */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Beliefs</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMindset.mindset?.beliefs && Array.isArray(selectedMemberMindset.mindset.beliefs) && selectedMemberMindset.mindset.beliefs.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMindset.mindset.beliefs.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No beliefs available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Body Language */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">Body Language</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      {selectedMemberMindset.mindset?.bodyLanguage && Array.isArray(selectedMemberMindset.mindset.bodyLanguage) && selectedMemberMindset.mindset.bodyLanguage.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedMemberMindset.mindset.bodyLanguage.map((item, index) => (
                            <li key={index} className="text-gray-600 text-sm">{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 text-sm">No body language available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Identity Statement */}
                  {selectedMemberMindset.mindset?.identityStatement && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-800 mb-2">Identity Statement</h4>
                      <div className="bg-gray-50 p-3 rounded-md space-y-2">
                        {selectedMemberMindset.mindset.identityStatement.iHave && selectedMemberMindset.mindset.identityStatement.iHave.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700">I Have:</h5>
                            <ul className="list-disc list-inside ml-4">
                              {selectedMemberMindset.mindset.identityStatement.iHave.map((item, index) => (
                                <li key={index} className="text-gray-600 text-sm">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedMemberMindset.mindset.identityStatement.iAm && selectedMemberMindset.mindset.identityStatement.iAm.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700">I Am:</h5>
                            <ul className="list-disc list-inside ml-4">
                              {selectedMemberMindset.mindset.identityStatement.iAm.map((item, index) => (
                                <li key={index} className="text-gray-600 text-sm">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedMemberMindset.mindset.identityStatement.iLove && selectedMemberMindset.mindset.identityStatement.iLove.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700">I Love:</h5>
                            <ul className="list-disc list-inside ml-4">
                              {selectedMemberMindset.mindset.identityStatement.iLove.map((item, index) => (
                                <li key={index} className="text-gray-600 text-sm">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedMemberMindset.mindset.identityStatement.iGive && selectedMemberMindset.mindset.identityStatement.iGive.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700">I Give:</h5>
                            <ul className="list-disc list-inside ml-4">
                              {selectedMemberMindset.mindset.identityStatement.iGive.map((item, index) => (
                                <li key={index} className="text-gray-600 text-sm">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(!selectedMemberMindset.mindset.identityStatement.iHave || selectedMemberMindset.mindset.identityStatement.iHave.length === 0) &&
                         (!selectedMemberMindset.mindset.identityStatement.iAm || selectedMemberMindset.mindset.identityStatement.iAm.length === 0) &&
                         (!selectedMemberMindset.mindset.identityStatement.iLove || selectedMemberMindset.mindset.identityStatement.iLove.length === 0) &&
                         (!selectedMemberMindset.mindset.identityStatement.iGive || selectedMemberMindset.mindset.identityStatement.iGive.length === 0) && (
                          <p className="text-gray-600 text-sm">No identity statement available</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-8">No Mindset data available for this team member.</p>
              )}
              
              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={closeMindsetModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachDashboard;
